<template>
  <!-- Grid View -->
  <a
    v-if="viewMode === 'grid'"
    :href="`${linkPrefix}/${tutor.slug}`"
    :class="[
      'bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl transition-all cursor-pointer flex flex-col h-full',
      hoverBorderClass
    ]"
  >
    <!-- Header with Avatar and Info -->
    <div class="sm:text-center mb-3 sm:mb-5 grid grid-cols-[100px_auto] md:block">
      <img
        :src="avatarUrl"
        :alt="tutor.name"
        :class="['w-20 h-20 row-span-3 rounded-full sm:mx-auto sm:mb-4 border-4', avatarBorderClass]"
      >
      <h3 class="font-bold col-2 text-gray-900 text-base sm:mb-1">{{ tutor.name }}</h3>
      <p class="text-sm col-2 text-gray-500 sm:mb-3">{{ formattedQualification }}</p>

      <div class="flex col-2 items-center sm:justify-center sm:space-x-4 text-sm gap-4">
        <div v-if="tutor.is_verified" class="flex items-center text-blue-600">
          <BadgeCheck class="w-4 h-4 mr-1" />
          <span class="font-semibold text-xs">Verified</span>
        </div>
        <!-- <div class="flex items-center text-gray-600">
          <Users class="w-4 h-4 mr-1.5" />
          <span>{{ formattedStudentCount }}</span>
        </div> -->
      </div>
    </div>

    <!-- Subject Badges -->
    <div class="flex flex-wrap gap-2 mb-4">
      <span
        v-for="subject in displaySubjects"
        :key="subject.id"
        :style="{ backgroundColor: subject.color + '20', color: subject.color }"
        class="inline-block px-3 py-1.5 rounded-full text-xs font-semibold"
      >
        {{ subject.name }}
      </span>
    </div>

    <!-- Bio -->
    <p class="text-sm text-gray-600 mb-5 leading-relaxed line-clamp-2">
      {{ tutor.bio }}
    </p>

    <!-- Bottom Section (always hugs bottom) -->
    <div class="mt-auto">
      <!-- Services Icons -->
      <div
          v-if="offeredServices.length > 0"
          class="flex gap-4 mb-5 pb-5 border-b border-gray-100"
          :class="{ 'justify-between': offeredServices.length === 4}"
      >
        <div
          v-for="service in offeredServices"
          :key="service.key"
          class="text-center flex sm:block items-center"
        >
          <component :is="service.icon" :class="['w-5 h-5 mr-2 mb-1.5', service.colorClass]" />
          <p class="text-xs text-gray-600 font-medium mb-1">
            {{ service.label }}
          </p>
        </div>
      </div>

    <div class="flex md:block justify-between items-center">

      <!-- Pricing -->
      <div class="sm:text-center mb-3">
        <p class="text-xs text-gray-500 mb-1">Starting from</p>
        <p class="text-2xl font-bold text-gray-900">
          £{{ startingPrice }}
          <span class="text-sm font-normal text-gray-500">/wk</span>
        </p>
      </div>

      <!-- CTA Button -->
      <button class="px-8 md:w-full bg-brand-blue text-white py-3 rounded-xl text-sm font-bold hover:bg-blue-600 transition-colors">
        View Plans
      </button>

    </div>

    </div>
  </a>

  <!-- List View (Horizontal Card) -->
  <a
    v-else
    :href="`${linkPrefix}/${tutor.slug}`"
    :class="[
      'bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-xl transition-all cursor-pointer flex gap-5',
      hoverBorderClass
    ]"
  >
    <!-- Left: Avatar with Services -->
    <div class="flex-shrink-0 flex flex-col items-center">
      <img
        :src="avatarUrl"
        :alt="tutor.name"
        :class="['w-20 h-20 rounded-full border-4 mb-3', avatarBorderClass]"
      >
      <!-- Services Icons (below avatar) -->
      <div v-if="offeredServices.length > 0" class="flex items-center gap-2">
        <div
          v-for="service in offeredServices"
          :key="service.key"
          class="flex flex-col items-center"
        >
          <component :is="service.icon" :class="['w-4 h-4', service.colorClass]" />
        </div>
      </div>
    </div>

    <!-- Middle: Main Content -->
    <div class="flex-grow">
      <div class="flex items-start justify-between mb-2">
        <div>
          <h3 class="font-bold text-gray-900 text-base mb-0.5">{{ tutor.name }}</h3>
          <p class="text-xs text-gray-500 mb-1.5">{{ formattedQualification }}</p>

          <div class="flex items-center space-x-3 text-xs">
            <div v-if="tutor.is_verified" class="flex items-center text-blue-600">
              <BadgeCheck class="w-3.5 h-3.5 mr-1" />
              <span class="font-semibold">Verified</span>
            </div>
            <!-- <div class="flex items-center text-gray-600">
              <Users class="w-3.5 h-3.5 mr-1" />
              <span>{{ formattedStudentCount }}</span>
            </div> -->
          </div>
        </div>

        <!-- Right: Pricing + Button -->
        <div class="text-right flex flex-col items-end">
          <div class="mb-2">
            <p class="text-xs text-gray-500 mb-0.5">Starting from</p>
            <p class="text-xl font-bold text-gray-900">
              £{{ startingPrice }}
              <span class="text-xs font-normal text-gray-500">/wk</span>
            </p>
          </div>
          <button class="bg-brand-blue text-white px-6 py-2 rounded-lg text-xs font-bold hover:bg-blue-600 transition-colors whitespace-nowrap">
            View Plans
          </button>
        </div>
      </div>

      <!-- Subject Badges -->
      <div class="flex flex-wrap gap-1.5 mb-2">
        <span
          v-for="subject in displaySubjects"
          :key="subject.id"
          :style="{ backgroundColor: subject.color + '20', color: subject.color }"
          class="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold"
        >
          {{ subject.name }}
        </span>
      </div>

      <!-- Bio -->
      <p class="text-xs text-gray-600 leading-relaxed line-clamp-2">
        {{ tutor.bio }}
      </p>
    </div>
  </a>
</template>

<script setup>
import { computed } from 'vue';
import { BadgeCheck, Users, Video, BookOpen } from 'lucide-vue-next';

const props = defineProps({
  tutor: {
    type: Object,
    required: true
  },
  colorIndex: {
    type: Number,
    default: 0
  },
  viewMode: {
    type: String,
    default: 'grid',
    validator: (value) => ['grid', 'list'].includes(value)
  },
  linkPrefix: {
    type: String,
    default: '/tutors'
  }
});

// Color themes for cards
const cardColors = [
  { hover: 'hover:border-blue-200', avatar: 'border-blue-100', button: 'bg-blue-500 hover:bg-blue-600' },
  { hover: 'hover:border-green-200', avatar: 'border-green-100', button: 'bg-green-500 hover:bg-green-600' },
  { hover: 'hover:border-purple-200', avatar: 'border-purple-100', button: 'bg-purple-500 hover:bg-purple-600' },
  { hover: 'hover:border-pink-200', avatar: 'border-pink-100', button: 'bg-pink-500 hover:bg-pink-600' },
  { hover: 'hover:border-teal-200', avatar: 'border-teal-100', button: 'bg-teal-500 hover:bg-teal-600' },
  { hover: 'hover:border-yellow-200', avatar: 'border-yellow-100', button: 'bg-yellow-500 hover:bg-yellow-600' },
  { hover: 'hover:border-red-200', avatar: 'border-red-100', button: 'bg-red-500 hover:bg-red-600' },
  { hover: 'hover:border-indigo-200', avatar: 'border-indigo-100', button: 'bg-indigo-500 hover:bg-indigo-600' },
];

const currentColor = computed(() => cardColors[props.colorIndex % cardColors.length]);
const hoverBorderClass = computed(() => currentColor.value.hover);
const avatarBorderClass = computed(() => currentColor.value.avatar);

// Format qualification (extract first university)
const formattedQualification = computed(() => {
  if (!props.tutor.qualification) {
    return '';
  }
  // Try to extract university name (e.g., "Oxford Graduate", "Cambridge PhD")
  return props.tutor.qualification;
});

// Generate initials from name
const initials = computed(() => {
  const parts = (props.tutor.name || '').split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || '?').toUpperCase();
});

// Generate avatar URL with initials fallback
const avatarUrl = computed(() => {
  if (props.tutor.avatar) return props.tutor.avatar;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(props.tutor.name || 'T')}&size=80&background=random&bold=true`;
});

// Format student count
const formattedStudentCount = computed(() => {
  const count = props.tutor.students_count || 0;
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k students`;
  }
  return `${count} students`;
});

// Display all subjects
const displaySubjects = computed(() => {
  return props.tutor.subjects || [];
});

// Calculate starting price (cheapest tier)
const startingPrice = computed(() => {
  const prices = [
    props.tutor.price_base,
    props.tutor.price_silver,
    props.tutor.price_gold
  ].filter(price => price > 0);

  return prices.length > 0 ? Math.min(...prices).toFixed(2) : '0.00';
});

// Services configuration
const serviceConfig = {
  videos: {
    key: 'videos',
    label: 'Videos',
    icon: Video,
    colorClass: 'text-blue-500'
  },
  group_sessions: {
    key: 'group_sessions',
    label: 'Groups',
    icon: Users,
    colorClass: 'text-green-500'
  },
  one_on_one: {
    key: 'one_on_one',
    label: '1:1',
    icon: Users,
    colorClass: 'text-purple-500'
  },
  materials: {
    key: 'materials',
    label: 'Materials',
    icon: BookOpen,
    colorClass: 'text-gray-600'
  }
};

// Get only services that are offered by the tutor
const offeredServices = computed(() => {
  const tutorServices = props.tutor.services_offered || [];

  return Object.values(serviceConfig).filter(service =>
    tutorServices.includes(service.key)
  );
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
